import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../components';
import {
    getSocialBrandAssets,
    uploadSocialBrandAsset,
    deleteSocialBrandAsset,
} from '../../util/api';
import { invalidateBrandAssets } from './DesignEditor/useBrandAssets';
import css from './AdminSocialPostsPage.css';

/**
 * BrandAssetsPanel — the self-serve brand-graphic manager, rendered as a section of
 * the social Settings form. Self-contained (its own local state, no duck): it lists
 * the uploaded library, uploads a new graphic (SVG/PNG/JPG), and deletes one with a
 * two-step inline confirm.
 *
 * The SERVER is the authority — it sanitizes every upload and blocks a delete that a
 * workflow or design preset still references (409). This panel surfaces those
 * German messages verbatim rather than second-guessing them. After any mutation it
 * calls invalidateBrandAssets() so the design editor's "Grafik" picker refreshes.
 */

const ACCEPT = '.svg,.png,.jpg,.jpeg';

const BrandAssetsPanel = () => {
    const [assets, setAssets] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [label, setLabel] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    // The id awaiting a second "Wirklich löschen?" click, and the id being deleted.
    const [confirmId, setConfirmId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const fileInputRef = useRef(null);

    const load = () => {
        getSocialBrandAssets()
            .then(rows => {
                setAssets(Array.isArray(rows) ? rows : []);
                setLoadError('');
            })
            .catch(e => setLoadError(e.message || 'Grafiken konnten nicht geladen werden.'));
    };

    useEffect(load, []);

    const resetFileInput = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const canUpload = !!file && !!label.trim() && !uploading;

    const handleUpload = () => {
        if (!canUpload) return;
        setUploading(true);
        setUploadError('');
        uploadSocialBrandAsset(file, label.trim())
            .then(() => {
                setLabel('');
                resetFileInput();
                load();
                invalidateBrandAssets();
            })
            .catch(e => setUploadError(e.message || 'Upload fehlgeschlagen.'))
            .finally(() => setUploading(false));
    };

    const handleDelete = id => {
        // First click arms the confirm; second click (on the same row) deletes.
        if (confirmId !== id) {
            setConfirmId(id);
            setDeleteError('');
            return;
        }
        setDeletingId(id);
        setDeleteError('');
        deleteSocialBrandAsset(id)
            .then(() => {
                setConfirmId(null);
                load();
                invalidateBrandAssets();
            })
            .catch(e => setDeleteError(e.message || 'Löschen fehlgeschlagen.'))
            .finally(() => setDeletingId(null));
    };

    return (
        <div className={css.brandAssets}>
            <div className={css.brandAssetUpload}>
                <div className={css.field}>
                    <label className={css.label} htmlFor="brandAssetLabel">
                        Name
                    </label>
                    <input
                        id="brandAssetLabel"
                        className={css.input}
                        type="text"
                        maxLength={120}
                        value={label}
                        placeholder="z. B. Logo (weiss)"
                        onChange={e => setLabel(e.target.value)}
                    />
                </div>
                <div className={css.field}>
                    <label className={css.label} htmlFor="brandAssetFile">
                        Datei (SVG, PNG oder JPG, max. 512 KB)
                    </label>
                    <input
                        id="brandAssetFile"
                        ref={fileInputRef}
                        className={css.input}
                        type="file"
                        accept={ACCEPT}
                        onChange={e => {
                            setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
                            setUploadError('');
                        }}
                    />
                </div>
                <div className={css.actionBarButtons}>
                    <Button
                        className={css.secondaryButton}
                        type="button"
                        inProgress={uploading}
                        disabled={!canUpload}
                        onClick={handleUpload}
                    >
                        Hochladen
                    </Button>
                </div>
                {uploadError && (
                    <p className={css.fieldError} role="alert">
                        {uploadError}
                    </p>
                )}
            </div>

            {loadError ? (
                <p className={css.fieldError} role="alert">
                    {loadError}
                </p>
            ) : assets.length === 0 ? (
                <p className={css.hint}>Noch keine eigenen Grafiken hochgeladen.</p>
            ) : (
                <div className={css.brandAssetGrid}>
                    {assets.map(asset => (
                        <div key={asset.id} className={css.brandAssetCard}>
                            <div className={css.brandAssetThumbWrap}>
                                <img
                                    className={css.brandAssetThumb}
                                    src={asset.src}
                                    alt={asset.label}
                                />
                            </div>
                            <span className={css.brandAssetLabel} title={asset.label}>
                                {asset.label}
                            </span>
                            <button
                                type="button"
                                className={`${css.brandAssetDelete} ${
                                    confirmId === asset.id ? css.brandAssetDeleteConfirm : ''
                                }`}
                                disabled={deletingId === asset.id}
                                onClick={() => handleDelete(asset.id)}
                                onBlur={() => confirmId === asset.id && setConfirmId(null)}
                            >
                                {deletingId === asset.id
                                    ? 'Wird gelöscht…'
                                    : confirmId === asset.id
                                    ? 'Wirklich löschen?'
                                    : 'Löschen'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {deleteError && (
                <p className={css.fieldError} role="alert">
                    {deleteError}
                </p>
            )}
        </div>
    );
};

export default BrandAssetsPanel;
